import React, { useState, useEffect } from 'react';
import { X, Download, ZoomIn, ZoomOut, RotateCw, FileText, AlertCircle } from 'lucide-react';
import api from '../../../../api/config';

/**
 * Componente para previsualizar documentos e imágenes
 * Soporta: PDF, imágenes (JPG, PNG, etc.)
 * Carga el archivo via axios (con auth) para evitar problemas de CORS/proxy.
 */
export default function DocumentPreview({ document, onClose, onDownload }) {
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);
  const [error, setError] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const [loadingBlob, setLoadingBlob] = useState(true);

  if (!document) return null;

  const fileName = document.archivo_nombre || 'documento';
  const fileType = document.archivo_tipo || '';

  const isImage = fileType.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp)$/i.test(fileName);
  const isPDF   = fileType === 'application/pdf' || fileName.toLowerCase().endsWith('.pdf');
  const canPreview = isImage || isPDF;

  // Cargar blob via API autenticada cuando se abre el preview
  useEffect(() => {
    if (!canPreview || !document.id) return;
    let objectUrl = null;
    setLoadingBlob(true);
    setError(false);
    api.get(`/employee-documents/${document.id}/download`, { responseType: 'blob' })
      .then(response => {
        objectUrl = window.URL.createObjectURL(response.data);
        setBlobUrl(objectUrl);
      })
      .catch(() => setError(true))
      .finally(() => setLoadingBlob(false));

    return () => {
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    };
  }, [document.id]); // eslint-disable-line

  const fullFileUrl = blobUrl;

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 25, 200));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 25, 50));
  };

  const handleRotate = () => {
    setRotation(prev => (prev + 90) % 360);
  };

  const handleImageError = () => {
    setError(true);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 flex flex-col z-50">
      {/* Header */}
      <div className="bg-gray-900 text-white p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FileText size={24} />
          <div>
            <h3 className="font-semibold text-lg">
              {document.documento_nombre || document.nombre}
            </h3>
            <p className="text-sm text-gray-400">{fileName}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Controles de zoom para imágenes */}
          {isImage && !error && (
            <>
              <button
                onClick={handleZoomOut}
                className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
                title="Alejar"
              >
                <ZoomOut size={20} />
              </button>
              <span className="text-sm px-2">{zoom}%</span>
              <button
                onClick={handleZoomIn}
                className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
                title="Acercar"
              >
                <ZoomIn size={20} />
              </button>
              <button
                onClick={handleRotate}
                className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
                title="Rotar"
              >
                <RotateCw size={20} />
              </button>
              <div className="w-px h-6 bg-gray-700 mx-2"></div>
            </>
          )}

          {/* Descargar */}
          {onDownload && (
            <button
              onClick={() => onDownload(document)}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors flex items-center gap-2"
            >
              <Download size={18} />
              Descargar
            </button>
          )}

          {/* Cerrar */}
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
            title="Cerrar"
          >
            <X size={24} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto bg-gray-800 flex items-center justify-center p-4">
        {loadingBlob ? (
          <div className="text-center text-white">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-3" />
            <p className="text-sm text-gray-400">Cargando documento...</p>
          </div>
        ) : !canPreview ? (
          <div className="bg-gray-900 rounded-xl p-8 text-center max-w-md">
            <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">
              Vista previa no disponible
            </h3>
            <p className="text-gray-400 mb-6">
              Este tipo de archivo no soporta vista previa en el navegador.
              <br />
              Tipo: {fileType || 'Desconocido'}
            </p>
            {onDownload && (
              <button
                onClick={() => onDownload(document)}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2 mx-auto"
              >
                <Download size={20} />
                Descargar para ver
              </button>
            )}
          </div>
        ) : error ? (
          <div className="bg-gray-900 rounded-xl p-8 text-center max-w-md">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">
              Error al cargar el archivo
            </h3>
            <p className="text-gray-400 mb-4">
              No se pudo cargar la vista previa del archivo.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              URL: {fullFileUrl}
            </p>
            {onDownload && (
              <button
                onClick={() => onDownload(document)}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2 mx-auto"
              >
                <Download size={20} />
                Intentar descargar
              </button>
            )}
          </div>
        ) : isPDF ? (
          // Vista previa de PDF
          <div className="w-full h-full bg-white rounded-lg overflow-hidden shadow-2xl">
            <iframe
              src={fullFileUrl}
              className="w-full h-full"
              title={fileName}
              onError={handleImageError}
            />
          </div>
        ) : (
          // Vista previa de imagen
          <div className="max-w-full max-h-full flex items-center justify-center">
            <img
              src={fullFileUrl}
              alt={fileName}
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              style={{
                transform: `scale(${zoom / 100}) rotate(${rotation}deg)`,
                transition: 'transform 0.2s ease-in-out'
              }}
              onError={handleImageError}
            />
          </div>
        )}
      </div>

      {/* Footer - Info */}
      <div className="bg-gray-900 text-white px-4 py-2 flex items-center justify-between text-sm">
        <div className="flex items-center gap-4">
          <span className="text-gray-400">Empleado:</span>
          <span>{document.empleado_nombre}</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-gray-400">Versión:</span>
          <span>v{document.version_actual}</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-gray-400">Tamaño:</span>
          <span>
            {document.archivo_tamano
              ? document.archivo_tamano < 1024
                ? `${document.archivo_tamano} B`
                : document.archivo_tamano < 1024 * 1024
                ? `${(document.archivo_tamano / 1024).toFixed(1)} KB`
                : `${(document.archivo_tamano / (1024 * 1024)).toFixed(1)} MB`
              : 'N/A'}
          </span>
        </div>
      </div>
    </div>
  );
}
